/**
 * SettlementsPage — Stories 2.5 + 2.6 (Settlement-Uebersicht + Payout-Sheet)
 *
 * Referenz (AUTORITATIV): claude-design-import/settlements.html.
 *
 * AC-1 (2.5): Tabelle mit Filtern nach Monat, Status und Host (serverseitig
 *   via Contract-Parameter month/status/host_id), Status-Pills in der Kette
 *   Entwurf → Freigegeben → Beglichen. meta.totals/meta.counts werden vom
 *   Hook weiterhin geliefert, aber seit dem Fix vom 2026-09-01 NICHT mehr
 *   als Summen-Kacheln angezeigt (Vorlage kennt keine Hero-Kacheln).
 * AC-2 (2.5) + 2.6: Payout-Sheet (PayoutSheet.tsx) mit Solana-Pay-QR,
 *   Legacy-Umschalter, Copy-Button, CSV-Export und PDF-Beleg.
 * AC-3 (2.5): Status-Umschalter (PATCH, optimistisch mit Rollback).
 * Generate:  POST /settlements/generate — der Contract antwortet SYNCHRON
 *   mit 201 (created/updated/skipped_locked); die im Auftrag genannte
 *   202+Polling-Variante ist im Contract nicht deklariert und wird als
 *   toleranter Fallback unterstuetzt (Details: useSettlementMutations).
 *
 * Dokumentierte Abweichungen zur Vorlage:
 *  - Der „Anteil“-Wert erscheint in EINER Waehrung (total_amount + currency)
 *    statt „$ · HNT“: ein Settlement hat laut Contract genau eine
 *    Auszahlungswaehrung; Umrechnungen ohne Kurs waeren erfunden.
 *  - Zusaetzlich zur Segment-Statusleiste gibt es Monat- und Host-Filter
 *    (AC-1 verlangt alle drei; die Vorlage zeigt nur das Status-Segment).
 *
 * Design-Angleichung 2026-09-01 (Audit visual-validation-2026-09-01.md):
 *  - Farbiger Monats-Badge pro Zeile (payout-commissions.html `identity-img`)
 *    und grosser/farbiger Anteilsbetrag (`.glow-text`) uebernommen.
 *  - Status-Pill nutzt jetzt die geteilte StatusPill-Komponente statt
 *    StatusChip/Eigenbau.
 *  - Bewusst NICHT uebernommen: der „Composition“-Balken der Vorlage
 *    zerlegt Commission/Fee-Anteile — das Settlement-Schema liefert diese
 *    Aufschluesselung nicht (nur total_amount), ein Balken waere erfunden.
 *
 * Fix 2026-09-01 (Abnahme-Findings settlements.html):
 *  - Grosse Gradient-Hero mit Stat-Kacheln entfernt: die Vorlage zeigt nur
 *    Titel + Status-Ketten-Legende in EINER Zeile neben Export/Generate.
 *    meta.totals/meta.counts werden dadurch nicht mehr angezeigt (Vorlage
 *    kennt keine Summen-Kacheln) — behoben zugleich das P2-Ellipsis-Problem
 *    abgeschnittener Zahlen, das nur in dieser Hero auftrat.
 *  - Tabelle auf das geteilte `.list`/`.list-row`-Kartenraster umgestellt
 *    (bereits produktiv in invoices/hosts/hotspots) statt <table>: HOST und
 *    PERIOD in einer Zelle, GB und HNT in einer „Basis“-Zelle kombiniert —
 *    das behebt zugleich den P0-Seiten-Overflow bei 390px, da das Grid bei
 *    900/390px Spalten einklappt statt sie unveraendert zu lassen.
 *
 * Fix 2026-09-01 (2. Abnahme-Runde, P1/P2 auf settlements.html):
 *  - RATE-Spalte ergaenzt: amount_from_gigabytes / gigabytes_transferred
 *    sind beides ECHTE Felder derselben Zeile, der Quotient ist der
 *    tatsaechlich angewandte GB-Satz (settlementUtils.fmtRatePerGb) — anders
 *    als die zuvor verworfene Naeherung aus total_amount/GB (die HNT-Basis-
 *    Anteile mischen wuerde) ist dieser Wert real, nur bei GB-Regeln > 0
 *    definiert (sonst "—").
 *  - Status-Pill-Ton auf die Vorlage ausgerichtet: draft=warning (gefuellt
 *    orange), approved=primary (blauer Ton), settled=success (gefuellt
 *    gruen) — vorher war draft faelschlich "neutral" (grauer Punkt).
 *  - Zeilen-Button-Hierarchie: btn-secondary (Outline) fuer draft/settled,
 *    btn-primary (gefuellt) nur fuer approved (die eigentliche Auszahlung) —
 *    vorher nutzten alle Status denselben gefuellten Button.
 *  - Host-Name-Umbruch bei <=900px: `overflow-wrap: anywhere` brach lange
 *    Namen zeichenweise mitten im Wort; `break-word` bevorzugt Wortgrenzen
 *    und bricht nur, wenn ein einzelnes Wort selbst nicht passt.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { StatusPill } from '../../components/StatusPill'
import { Pagination } from '../../components/Pagination'
import { PremiumRequired } from '../../components/PremiumRequired'
import { useAccount } from '../settings/useAccount'
import { isPremiumRequiredError } from '../../lib/premiumError'
import { PayoutSheet } from './PayoutSheet'
import {
  useSettlements,
  useDueSettlements,
  useHostOptions,
  type Settlement,
  type SettlementStatus,
} from './useSettlements'
import {
  useGenerateSettlements,
  downloadSettlementsCsv,
  type GenerateMeta,
} from './useSettlementMutations'
import {
  fmtAmount,
  fmtDecimal,
  fmtPeriod,
  fmtRatePerGb,
  monthAbbrev,
  monthTone,
  previousPeriodMonth,
  usagePct,
} from './settlementUtils'
import './settlements.css'

type StatusFilter = 'all' | SettlementStatus
const STATUS_FILTERS: StatusFilter[] = ['all', 'draft', 'approved', 'settled']

// --- Status-Pill (Kette Entwurf → Freigegeben → Beglichen) -------------------

// Fix Abnahme-Finding P1 (Status-Pills ohne Farbcodierung): Ton 1:1 wie in
// der Vorlage settlements.html (`stPill`) — draft=pill-warning,
// approved=pill-primary, settled=pill-success.
const STATUS_TONE: Record<SettlementStatus, 'success' | 'primary' | 'warning'> = {
  draft: 'warning',
  approved: 'primary',
  settled: 'success',
}

export function SettlementStatusPill({
  status,
  id,
}: {
  status: SettlementStatus | undefined
  id: number | undefined
}) {
  const { t } = useTranslation('settlements')
  const key = status ?? 'draft'
  return (
    <StatusPill
      variant={STATUS_TONE[key]}
      label={t(`status.${key}.label`)}
      title={t(`status.${key}.tooltip`)}
      data-testid={`settlement-status-${id ?? 'x'}`}
    />
  )
}

// --- Kopfzeile (Vorlage: Titel + Status-Ketten-Legende in einer Zeile) ------

function SettlementsHeaderCopy() {
  const { t } = useTranslation('settlements')
  return (
    <div className="stl-header-copy">
      <h1>{t('title')}</h1>
      <div className="stl-flow" data-testid="stl-flow">
        <span className="stl-flow-step stl-flow-draft">{t('status.draft.label')}</span>
        <span className="stl-flow-arrow" aria-hidden="true">
          →
        </span>
        <span className="stl-flow-step stl-flow-approved">{t('status.approved.label')}</span>
        <span className="stl-flow-arrow" aria-hidden="true">
          →
        </span>
        <span className="stl-flow-step stl-flow-settled">{t('status.settled.label')}</span>
        <span className="stl-flow-note">{t('chainNote')}</span>
      </div>
    </div>
  )
}

// --- Tabellenzeile (Kartenraster wie invoices/hosts: .list + .list-row) -----

function SettlementRow({
  settlement,
  onOpenPayout,
}: {
  settlement: Settlement
  onOpenPayout: (s: Settlement) => void
}) {
  const { t, i18n } = useTranslation('settlements')
  const noValue = t('noValue')

  const period = fmtPeriod(settlement.period_month, i18n.language) ?? noValue
  const gb = fmtDecimal(settlement.gigabytes_transferred, 1) ?? noValue
  const hnt = fmtDecimal(settlement.hnt_rewards, 2) ?? noValue
  // Listenzeile ist ein Scan-Ueberblick, keine Zahlungsbestaetigung: 2
  // Nachkommastellen statt der vollen HNT-Praezision (die bleibt im
  // Payout-Sheet erhalten, wo der Betrag gegen den QR-Code geprueft wird —
  // Fix Abnahme-Finding "12.50000000 HNT sieht wie ein Formatierungsbug aus").
  const amount = fmtAmount(settlement.total_amount, settlement.currency, 2) ?? noValue
  const basis = `${gb} GB · ${hnt} HNT`
  const rate = fmtRatePerGb(settlement) ?? noValue
  // Status-Kette Entwurf -> Freigegeben -> Beglichen: der Zeilen-Button darf
  // fuer einen Entwurf nicht "Auszahlen" heissen (das suggeriert eine direkte
  // Auszahlung ohne Freigabe-Schritt). Das Sheet selbst bietet ohnehin den
  // richtigen naechsten Schritt an (sheet.approve/sheet.markSettled) — hier
  // nur das Zeilen-Label an den Status anpassen (Fix Abnahme-Finding P1).
  const rowActionLabel =
    settlement.status === 'settled'
      ? t('table.details')
      : settlement.status === 'draft'
        ? t('sheet.approve')
        : t('table.payout')
  // Fix Abnahme-Finding P2 (Button-Hierarchie): Vorlage nutzt Outline
  // ("Approve") fuer Entwuerfe und einen gefuellten Primaer-Button nur fuer
  // die eigentliche Auszahlung ("Pay…") — settled zeigt ohnehin nur noch
  // einen ruhigen Beleg-Zugriff, kein Primaer-Call-to-Action.
  const rowActionClass = settlement.status === 'approved' ? 'btn-primary' : 'btn-secondary'

  return (
    <div className="list-row stl-grid" data-testid={`settlement-row-${settlement.id}`}>
      <span
        className={`stl-month-badge stl-tone-${monthTone(settlement.period_month)}`}
        aria-hidden="true"
      >
        {monthAbbrev(settlement.period_month, i18n.language)}
      </span>
      <div className="stl-host-cell">
        <div className="ellipsis">{settlement.host_name ?? noValue}</div>
        <div className="muted stl-host-sub">
          {period}
          {settlement.invoice_number ? ` · ${settlement.invoice_number}` : ''}
        </div>
      </div>
      <div className="stl-num stl-col-md">
        <span className="mono tnum">{basis}</span>
        {/* Rein dekorativer Nutzungsbalken (Vorlage `.comp`) — nutzt den
            echten GB-Wert auf einer fixen Skala, keine erfundene Quote. */}
        <div className="stl-usage-bar" aria-hidden="true">
          <div
            className="stl-usage-bar-fill"
            style={{ width: `${usagePct(settlement.gigabytes_transferred)}%` }}
          />
        </div>
      </div>
      <span className="mono tnum stl-num stl-col-md">{rate}</span>
      <span className="mono tnum stl-num stl-amount">{amount}</span>
      <span className="stl-col-md">
        <SettlementStatusPill status={settlement.status} id={settlement.id} />
      </span>
      <div className="row stl-row-actions">
        <button
          type="button"
          className={`btn btn-sm ${rowActionClass}`}
          onClick={() => onOpenPayout(settlement)}
          data-testid={`settlement-pay-${settlement.id}`}
        >
          {rowActionLabel}
        </button>
      </div>
      <div className="stl-mobile-meta">
        {basis} · {rate} · {t(`status.${settlement.status ?? 'draft'}.label`)}
      </div>
    </div>
  )
}

// --- Generate-Ergebnis -------------------------------------------------------

/** true, wenn der Lauf durchlief, aber rein gar nichts bewirkt hat. */
function isEmptyRun(meta: GenerateMeta): boolean {
  return (meta.created ?? 0) === 0 && (meta.updated ?? 0) === 0 && (meta.skipped_locked ?? 0) === 0
}

function GenerateResultHint({
  meta,
  pending,
  failed,
}: {
  meta: GenerateMeta | undefined | null
  pending: boolean
  failed: boolean
}) {
  const { t } = useTranslation('settlements')
  if (pending) {
    return (
      <div className="hint stl-generate-hint" role="status" data-testid="stl-generate-polling">
        {t('generate.polling')}
      </div>
    )
  }
  if (failed) {
    return (
      <div className="hint stl-generate-hint" role="alert" data-testid="stl-generate-error">
        {t('generate.failed')}
      </div>
    )
  }
  if (!meta) return null
  // Contract liefert keinen Grund fuer einen Nulllauf — der einzige bekannte
  // Grund ist ein fehlendes Provisions-Regelwerk (kein Share deckt den
  // Zeitraum ab), deshalb wird genau dieser Fall statt der generischen
  // "0/0/0"-Zusammenfassung erklaert (FIN-BILLING-SILENT-FAIL).
  if (isEmptyRun(meta)) {
    return (
      <div className="hint stl-generate-hint" role="status" data-testid="stl-generate-empty">
        {t('generate.resultEmpty')}{' '}
        <Link to="/splits" data-testid="stl-generate-empty-link">
          {t('generate.resultEmptyLink')}
        </Link>
      </div>
    )
  }
  return (
    <div className="hint stl-generate-hint" role="status" data-testid="stl-generate-result">
      {t('generate.result', {
        created: meta.created ?? 0,
        updated: meta.updated ?? 0,
        skipped: meta.skipped_locked ?? 0,
      })}
    </div>
  )
}

// --- Seite -------------------------------------------------------------------

export function SettlementsPage() {
  const { t, i18n } = useTranslation('settlements')
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<StatusFilter>('all')
  const [month, setMonth] = useState('')
  const [hostId, setHostId] = useState<number | undefined>(undefined)
  // F8 (Welle-4b): NUR die id merken und die Zeile bei jedem Render frisch aus
  // der Liste holen — eine eingefrorene Objektkopie zeigte nach einem
  // Statuswechsel weiter den alten Status (inkl. erneut angebotener Aktion).
  // Der Snapshot dient nur als Rueckfall, falls die Zeile nach einem
  // Filter-/Seitenwechsel nicht mehr in der geladenen Liste steckt.
  const [payoutTargetId, setPayoutTargetId] = useState<number | undefined>(undefined)
  const [payoutSnapshot, setPayoutSnapshot] = useState<Settlement | undefined>(undefined)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [generateMeta, setGenerateMeta] = useState<GenerateMeta | undefined | null>(null)
  const [generateFailed, setGenerateFailed] = useState(false)
  const [csvFailed, setCsvFailed] = useState(false)

  const filters = {
    month: month || undefined,
    status: status === 'all' ? undefined : status,
    hostId,
  }

  const { account } = useAccount()
  const { settlements, pagy, isLoading, isError, error, refetch } = useSettlements(page, filters)
  const { hosts } = useHostOptions()
  const generate = useGenerateSettlements()
  // Faellige (approved) Settlements fuer die Sammel-Auszahlung — unabhaengig
  // vom Status-Segment der Seite, aber mit denselben Monats-/Host-Filtern.
  // WP-D: der Sammel-Endpunkt (POST /api/v2/commissions/pay) erfasst
  // AUSSCHLIESSLICH approved HNT-Settlements — EUR/USD bleiben unangetastet
  // (Fiat laeuft ueber PATCH /api/v2/settlements/:id) — der CTA muss deshalb
  // auf HNT gefiltert sein, sonst zaehlt/aktiviert er ihn faelschlich fuer
  // faellige Fiat-Abrechnungen, die die Sammel-Auszahlung gar nicht erfasst.
  const { settlements: dueSettlements } = useDueSettlements({
    month: filters.month,
    hostId: filters.hostId,
  })
  const dueHntSettlements = dueSettlements.filter((s) => s.currency === 'HNT')

  const targetMonth = previousPeriodMonth(new Date())
  const targetMonthLabel = fmtPeriod(targetMonth, i18n.language) ?? targetMonth

  const changeFilter =
    <T,>(setter: (v: T) => void) =>
    (value: T) => {
      setter(value)
      setPage(1)
    }

  const openPayout = (s: Settlement) => {
    setPayoutTargetId(s.id)
    setPayoutSnapshot(s)
    setSheetOpen(true)
  }

  // WP-D: navigiert zur eigenstaendigen Sammel-Auszahlungsseite statt (wie
  // vormals WP12) ein Sheet inline zu oeffnen — der neue Batch-Endpunkt
  // gruppiert serverseitig in Bloecke, das braucht eine eigene URL fuer die
  // Wiederaufnahme nach Reload (siehe BatchPayoutPage.tsx).
  const openBatch = () => {
    void navigate({ to: '/settlements/payout' })
  }

  const runGenerate = async () => {
    setGenerateFailed(false)
    setGenerateMeta(null)
    try {
      const result = await generate.mutateAsync(undefined)
      if (result.kind === 'timeout') {
        setGenerateFailed(true)
        return
      }
      setGenerateMeta(result.meta)
    } catch {
      setGenerateFailed(true)
    }
  }

  const exportCsv = async () => {
    setCsvFailed(false)
    try {
      await downloadSettlementsCsv(filters)
    } catch {
      setCsvFailed(true)
    }
  }

  const payoutTarget =
    (payoutTargetId !== undefined
      ? settlements?.find((s) => s.id === payoutTargetId)
      : undefined) ?? payoutSnapshot

  const hasRows = !isLoading && !isError && !!settlements && settlements.length > 0
  const isEmpty = !isLoading && !isError && !!settlements && settlements.length === 0

  if ((account && !account.is_premium) || isPremiumRequiredError(error)) {
    return <PremiumRequired message={t('premiumRequired')} data-testid="settlements-premium-required" />
  }

  return (
    <section data-testid="settlements-page">
      <div className="stl-header">
        <SettlementsHeaderCopy />
        <div className="row stl-header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void exportCsv()}
            data-testid="stl-export-csv"
          >
            {t('actions.exportCsv')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={openBatch}
            disabled={dueHntSettlements.length === 0}
            data-testid="stl-batch-payout"
          >
            {dueHntSettlements.length > 0
              ? t('batch.ctaCount', { count: dueHntSettlements.length })
              : t('batch.ctaEmpty')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void runGenerate()}
            disabled={generate.isPending}
            data-testid="stl-generate"
          >
            {generate.isPending
              ? t('generate.pending')
              : t('generate.cta', { month: targetMonthLabel })}
          </button>
        </div>
      </div>

      {/* --- Filterbar ------------------------------------------------------- */}
      <div className="filterbar stl-filterbar">
        <div className="segment stl-segment" role="group" aria-label={t('filter.statusAria')}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={status === f ? 'is-active' : undefined}
              aria-pressed={status === f}
              onClick={() => changeFilter(setStatus)(f)}
              data-testid={`stl-filter-${f}`}
            >
              {t(`filter.${f}`)}
            </button>
          ))}
        </div>

        <div className="stl-month-wrap">
          <input
            type="month"
            className="input stl-month-input"
            value={month}
            onChange={(e) => changeFilter(setMonth)(e.target.value)}
            aria-label={t('filter.monthAria')}
            data-testid="stl-filter-month"
          />
          {/* #25: der native month-Input zeigt leer "-- ----" statt eines
              Platzhaltertexts. Ueberlagerung mit pointer-events:none, damit
              Klicks weiterhin den Picker oeffnen. */}
          {!month && (
            <span className="stl-month-placeholder" aria-hidden="true">
              {t('filter.monthPlaceholder')}
            </span>
          )}
        </div>

        <select
          className="input stl-host-select"
          value={hostId ?? ''}
          onChange={(e) =>
            changeFilter(setHostId)(e.target.value === '' ? undefined : Number(e.target.value))
          }
          aria-label={t('filter.hostAria')}
          data-testid="stl-filter-host"
        >
          <option value="">{t('filter.allHosts')}</option>
          {hosts.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
        </select>
      </div>

      {csvFailed && (
        <div className="hint stl-generate-hint" role="alert" data-testid="stl-csv-error">
          {t('actions.exportCsvFailed')}
        </div>
      )}
      <GenerateResultHint
        meta={generateMeta}
        pending={generate.isPending}
        failed={generateFailed}
      />

      {/* --- Tabelle --------------------------------------------------------- */}
      {isLoading && (
        <div className="stl-list" aria-busy="true" aria-label={t('loading')}>
          <div className="stl-skeleton">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="skeleton stl-skeleton-row" aria-hidden="true" key={i} />
            ))}
          </div>
        </div>
      )}

      {isError && (
        <div className="card stl-state" role="alert" data-testid="stl-error">
          <p>{t('error.load')}</p>
          <button type="button" className="btn btn-primary stl-state-cta" onClick={refetch}>
            {t('error.retry')}
          </button>
        </div>
      )}

      {isEmpty && (
        <div className="card stl-state" data-testid="stl-empty">
          <p className="stl-state-title">{t('empty.title')}</p>
          <p className="muted stl-state-hint">{t('empty.hint')}</p>
          <button
            type="button"
            className="btn btn-primary stl-state-cta"
            onClick={() => void runGenerate()}
            disabled={generate.isPending}
            aria-busy={generate.isPending}
            data-testid="stl-empty-generate"
          >
            {generate.isPending
              ? t('generate.pending')
              : t('generate.cta', { month: targetMonthLabel })}
          </button>
        </div>
      )}

      {hasRows && (
        <>
          <div className="list-head stl-grid" data-testid="stl-table-head">
            <span className="ui-sr-only">{t('table.period')}</span>
            <div>{t('table.hostPeriod')}</div>
            <div className="stl-num stl-col-md">{t('table.basis')}</div>
            <div className="stl-num stl-col-md">{t('table.rate')}</div>
            <div className="stl-num">{t('table.amount')}</div>
            <div className="stl-col-md">{t('table.status')}</div>
            <span className="ui-sr-only">{t('table.actions')}</span>
          </div>
          <div className="list stl-list" role="list" data-testid="stl-table">
            {settlements.map((s) => (
              <SettlementRow key={s.id} settlement={s} onOpenPayout={openPayout} />
            ))}
          </div>
          <Pagination
            page={pagy?.page ?? page}
            pages={pagy?.pages ?? 1}
            count={pagy?.count ?? settlements.length}
            onPageChange={setPage}
          />
        </>
      )}

      <PayoutSheet settlement={payoutTarget} open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </section>
  )
}
